/**
 * src/components/icons.tsx — the admin app's icon set.
 *
 * Font Awesome Free, replacing lucide-react (Luke, 2026-08-18: one icon set
 * across the whole product). Every component here keeps the NAME and the PROP
 * SHAPE the Lucide equivalent had, so the 117 files that use them changed only
 * their import path — not a single line of JSX.
 *
 * 🔒 ATTRIBUTION. Font Awesome Free icons are CC BY 4.0, which requires
 * attribution: "Font Awesome Free by @fontawesome — https://fontawesome.com,
 * icons licensed CC BY 4.0". The marketing site carries this in its rendered
 * HTML (each inlined SVG keeps FA's own comment). This app is behind auth and
 * renders from icon DEFINITIONS rather than SVG files, which carry no comment —
 * so this notice is the attribution. Do not delete it.
 *
 * 🔥 WHY NOT @fortawesome/react-fontawesome
 * That wrapper brings fontawesome-svg-core with it and exposes FA's own prop
 * API, which is not Lucide's. Rendering the path data directly is smaller and —
 * more importantly — lets these components accept `size`/`color`/`strokeWidth`
 * exactly as before, which is what makes this a one-line-per-file migration
 * instead of a 117-file rewrite.
 *
 * 🔥 `strokeWidth` IS ACCEPTED AND IGNORED, ON PURPOSE. Font Awesome solid
 * glyphs are filled shapes with no stroke, but ~20 call sites pass strokeWidth.
 * Swallowing it here keeps those call sites valid; letting it through would
 * reach the DOM and log "React does not recognize the strokeWidth prop"
 * warnings on every render.
 *
 * ⚠️ FA viewBoxes are NOT square (Lucide's are all 24×24). Width is derived
 * from each glyph's own aspect ratio and only height is pinned, so icons align
 * on height in a row of text instead of letterboxing.
 *
 * GENERATED — see scratchpad/gen_shim.py. To add an icon, add the import and
 * the export; keep them alphabetical.
 */

import type { CSSProperties, SVGProps } from 'react';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import {
  faArrowLeft,
  faArrowRight,
  faArrowRotateLeft,
  faArrowTrendUp,
  faArrowTurnDown,
  faArrowUpRightFromSquare,
  faArrowsRotate,
  faBan,
  faBarcode,
  faBell,
  faBolt,
  faBox,
  faBoxArchive,
  faBoxOpen,
  faBoxesPacking,
  faBoxesStacked,
  faBuilding,
  faBullhorn,
  faCalendar,
  faCalendarDay,
  faCalendarDays,
  faCalendarPlus,
  faCamera,
  faChartColumn,
  faCheck,
  faCheckDouble,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircle,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCirclePlay,
  faCircleQuestion,
  faCircleXmark,
  faClipboardCheck,
  faClipboardList,
  faClock,
  faCloud,
  faCoins,
  faComment,
  faCommentDots,
  faCopy,
  faCreditCard,
  faDollarSign,
  faDoorOpen,
  faDroplet,
  faEnvelope,
  faEye,
  faFileLines,
  faFilter,
  faFire,
  faFloppyDisk,
  faGift,
  faHandHoldingDollar,
  faHouse,
  faImage,
  faInbox,
  faKey,
  faLayerGroup,
  faLifeRing,
  faLocationArrow,
  faLocationDot,
  faLock,
  faMagnifyingGlass,
  faMap,
  faMinus,
  faMobileScreenButton,
  faPaperPlane,
  faPencil,
  faPercent,
  faPlus,
  faPowerOff,
  faPrint,
  faReceipt,
  faRightFromBracket,
  faRightToBracket,
  faRotateLeft,
  faRoute,
  faShieldHalved,
  faSliders,
  faSort,
  faSpinner,
  faSquareCheck,
  faTableCellsLarge,
  faTableColumns,
  faTag,
  faTrash,
  faTriangleExclamation,
  faTruck,
  faUnlock,
  faUser,
  faUserPlus,
  faUsers,
  faWandMagicSparkles,
  faWarehouse,
  faWifi,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref' | 'color'> {
  /** Rendered height in px. Width follows the glyph's own aspect ratio. */
  size?: number | string;
  color?: string;
  /** Accepted for source compatibility with the Lucide call sites. Ignored. */
  strokeWidth?: number | string;
  style?: CSSProperties;
}

/** An icon component. Replaces lucide-react's `LucideIcon`. */
export type IconComponent = (props: IconProps) => JSX.Element;

function make(def: IconDefinition, displayName: string): IconComponent {
  const [vw, vh, , , path] = def.icon;
  const d = Array.isArray(path) ? path.join(' ') : path;
  const Icon = ({ size = 24, color, style, strokeWidth: _ignored, ...rest }: IconProps) => {
    const h = typeof size === 'number' ? size : parseFloat(size);
    const w = Number.isFinite(h) ? Math.round(h * (vw / vh)) : size;
    return (
      <svg
        viewBox={`0 0 ${vw} ${vh}`}
        width={w}
        height={size}
        fill="currentColor"
        style={color ? { color, ...style } : style}
        {...rest}
      >
        <path d={d} />
      </svg>
    );
  };
  (Icon as { displayName?: string }).displayName = displayName;
  return Icon;
}

export const AlertCircle = make(faCircleExclamation, 'AlertCircle');
export const AlertOctagon = make(faTriangleExclamation, 'AlertOctagon');
export const AlertTriangle = make(faTriangleExclamation, 'AlertTriangle');
export const Archive = make(faBoxArchive, 'Archive');
export const ArrowLeft = make(faArrowLeft, 'ArrowLeft');
export const ArrowRight = make(faArrowRight, 'ArrowRight');
export const Ban = make(faBan, 'Ban');
export const BarChart3 = make(faChartColumn, 'BarChart3');
export const Bell = make(faBell, 'Bell');
export const BellRing = make(faBell, 'BellRing');
export const Boxes = make(faBoxesStacked, 'Boxes');
export const Building2 = make(faBuilding, 'Building2');
export const Calendar = make(faCalendar, 'Calendar');
export const CalendarClock = make(faCalendarDay, 'CalendarClock');
export const CalendarDays = make(faCalendarDays, 'CalendarDays');
export const CalendarPlus = make(faCalendarPlus, 'CalendarPlus');
export const Camera = make(faCamera, 'Camera');
export const Check = make(faCheck, 'Check');
export const CheckCheck = make(faCheckDouble, 'CheckCheck');
export const CheckCircle = make(faCircleCheck, 'CheckCircle');
export const CheckCircle2 = make(faCircleCheck, 'CheckCircle2');
export const CheckSquare = make(faSquareCheck, 'CheckSquare');
export const ChevronDown = make(faChevronDown, 'ChevronDown');
export const ChevronLeft = make(faChevronLeft, 'ChevronLeft');
export const ChevronRight = make(faChevronRight, 'ChevronRight');
export const ChevronUp = make(faChevronUp, 'ChevronUp');
export const ChevronsUpDown = make(faSort, 'ChevronsUpDown');
export const Circle = make(faCircle, 'Circle');
export const ClipboardCheck = make(faClipboardCheck, 'ClipboardCheck');
export const ClipboardList = make(faClipboardList, 'ClipboardList');
export const Clock = make(faClock, 'Clock');
export const CloudOff = make(faCloud, 'CloudOff');
export const Coins = make(faCoins, 'Coins');
export const Copy = make(faCopy, 'Copy');
export const CornerDownLeft = make(faArrowTurnDown, 'CornerDownLeft');
export const CreditCard = make(faCreditCard, 'CreditCard');
export const DollarSign = make(faDollarSign, 'DollarSign');
export const DoorOpen = make(faDoorOpen, 'DoorOpen');
export const Droplet = make(faDroplet, 'Droplet');
export const ExternalLink = make(faArrowUpRightFromSquare, 'ExternalLink');
export const Eye = make(faEye, 'Eye');
export const FileText = make(faFileLines, 'FileText');
export const Filter = make(faFilter, 'Filter');
export const Flame = make(faFire, 'Flame');
export const Gift = make(faGift, 'Gift');
export const HandCoins = make(faHandHoldingDollar, 'HandCoins');
export const HelpCircle = make(faCircleQuestion, 'HelpCircle');
export const Home = make(faHouse, 'Home');
export const ImageIcon = make(faImage, 'ImageIcon');
export const ImageOff = make(faImage, 'ImageOff');
export const Inbox = make(faInbox, 'Inbox');
export const Info = make(faCircleInfo, 'Info');
export const KeyRound = make(faKey, 'KeyRound');
export const Layers = make(faLayerGroup, 'Layers');
export const LayoutDashboard = make(faTableColumns, 'LayoutDashboard');
export const LayoutGrid = make(faTableCellsLarge, 'LayoutGrid');
export const LifeBuoy = make(faLifeRing, 'LifeBuoy');
export const Loader2 = make(faSpinner, 'Loader2');
export const Lock = make(faLock, 'Lock');
export const LogIn = make(faRightToBracket, 'LogIn');
export const LogOut = make(faRightFromBracket, 'LogOut');
export const Mail = make(faEnvelope, 'Mail');
export const Map = make(faMap, 'Map');
export const MapPin = make(faLocationDot, 'MapPin');
export const Megaphone = make(faBullhorn, 'Megaphone');
export const MessageCircle = make(faComment, 'MessageCircle');
export const MessageSquare = make(faComment, 'MessageSquare');
export const MessageSquareText = make(faCommentDots, 'MessageSquareText');
export const Minus = make(faMinus, 'Minus');
export const Navigation = make(faLocationArrow, 'Navigation');
export const Package = make(faBox, 'Package');
export const PackageCheck = make(faBoxesPacking, 'PackageCheck');
export const PackageOpen = make(faBoxOpen, 'PackageOpen');
export const PackagePlus = make(faBoxesStacked, 'PackagePlus');
export const Pencil = make(faPencil, 'Pencil');
export const Percent = make(faPercent, 'Percent');
export const PlayCircle = make(faCirclePlay, 'PlayCircle');
export const Plus = make(faPlus, 'Plus');
export const Power = make(faPowerOff, 'Power');
export const Printer = make(faPrint, 'Printer');
export const Receipt = make(faReceipt, 'Receipt');
export const RefreshCw = make(faArrowsRotate, 'RefreshCw');
export const RotateCcw = make(faRotateLeft, 'RotateCcw');
export const Route = make(faRoute, 'Route');
export const Save = make(faFloppyDisk, 'Save');
export const ScanLine = make(faBarcode, 'ScanLine');
export const Search = make(faMagnifyingGlass, 'Search');
export const Send = make(faPaperPlane, 'Send');
export const Shield = make(faShieldHalved, 'Shield');
export const ShieldCheck = make(faShieldHalved, 'ShieldCheck');
export const Sliders = make(faSliders, 'Sliders');
export const Smartphone = make(faMobileScreenButton, 'Smartphone');
export const Sparkles = make(faWandMagicSparkles, 'Sparkles');
export const Tag = make(faTag, 'Tag');
export const Trash2 = make(faTrash, 'Trash2');
export const TrendingUp = make(faArrowTrendUp, 'TrendingUp');
export const Truck = make(faTruck, 'Truck');
export const Undo2 = make(faArrowRotateLeft, 'Undo2');
export const UnlockKeyhole = make(faUnlock, 'UnlockKeyhole');
export const User = make(faUser, 'User');
export const UserPlus = make(faUserPlus, 'UserPlus');
export const Users = make(faUsers, 'Users');
export const Warehouse = make(faWarehouse, 'Warehouse');
export const Wifi = make(faWifi, 'Wifi');
export const WifiOff = make(faWifi, 'WifiOff');
export const X = make(faXmark, 'X');
export const XCircle = make(faCircleXmark, 'XCircle');
export const Zap = make(faBolt, 'Zap');
